import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Phone,
  Mail,
  Calendar,
  Heart,
  Users,
  Building2,
  Presentation,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { trackContactAction } from "@/utils/contactTracking";

interface EventContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EventContactModal: React.FC<EventContactModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useLanguage();

  // Clear variables when modal closes
  const handleClose = () => {
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];

      // Clear all modal and button variables to prevent pollution
      window.dataLayer.push({
        // Clear button variables
        button_text: undefined,
        button_location: undefined,
        // Clear modal variables
        modal_type: undefined,
        modal_name: undefined,
        parent_modal: undefined,
        page_location: undefined,
        // Clear consultation variables
        event_type: undefined,
        consultation_method: undefined,
        service_category: undefined,
      });
    }

    // Call the original onClose
    onClose();
  };

  // Track modal open
  useEffect(() => {
    if (isOpen && typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];

      // Clear all tracking variables first (prevents data pollution)
      // NOTE: button_text and button_location are preserved to retain context of how modal was opened
      window.dataLayer.push({
        // Clear modal variables (will be reset below)
        modal_type: undefined,
        modal_name: undefined,
        parent_modal: undefined,
        page_location: undefined,
        // Preserve button variables (retain context of triggering action)
        // button_text: undefined,     // ← Keep this!
        // button_location: undefined, // ← Keep this!
        // Clear booking variables
        room_type: undefined,
        // Clear contact action variables
        contact_type: undefined,
        contact_source: undefined,
        event_category: undefined,
        event_label: undefined,
        phone_number: undefined,
        email_address: undefined,
        whatsapp_number: undefined,
        region: undefined,
        destination: undefined,
        booking_method: undefined,
        // Clear event consultation variables
        event_type: undefined,
        consultation_method: undefined,
        service_category: undefined,
        // Clear Calendly variables
        calendly_action: undefined,
        calendly_location: undefined,
      });

      // Now push the actual event with clean data
      window.dataLayer.push({
        event: "contact_event_planning_modal_open",
        modal_type: "contact",
        modal_name: "event_contact",
        page_location: window.location.pathname,
      });
    }
  }, [isOpen]);

  const eventTypes = [
    {
      icon: Heart,
      title: "Wedding Planning",
      description:
        "Luxury ceremonies, receptions, and complete wedding packages",
      color: "from-rose-500 to-pink-500",
      eventType: "wedding" as const,
    },
    {
      icon: Users,
      title: "Private Parties",
      description:
        "Birthday celebrations, anniversaries, and intimate gatherings",
      color: "from-purple-500 to-indigo-500",
      eventType: "private_party" as const,
    },
    {
      icon: Building2,
      title: "Corporate Events",
      description: "Team building, product launches, and company celebrations",
      color: "from-blue-500 to-cyan-500",
      eventType: "corporate" as const,
    },
    {
      icon: Presentation,
      title: "Conference Rooms & AV",
      description:
        "Professional meeting spaces with state-of-the-art equipment",
      color: "from-amber-500 to-orange-500",
      eventType: "corporate" as const,
    },
  ];

  const contactMethods = [
    {
      icon: Phone,
      title: "Call Events Team",
      description: "Speak directly with our event specialists",
      contact: "+41 33 841 00 21",
      action: "tel:+41338410021",
      primary: true,
    },
    {
      icon: Mail,
      title: "Email Us",
      description: "Send us your event requirements",
      contact: "events@beatenberg-resortspa.ch",
      action: "mailto:events@beatenberg-resortspa.ch",
      primary: false,
    },
    {
      icon: Calendar,
      title: "Schedule Consultation",
      description: "Book a free consultation call",
      contact: "Book Now",
      action: "#calendly",
      primary: true,
    },
  ];

  // Skip portal during SSR
  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <AnimatePresence data-oid=":riotip">
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10000]"
            style={{ zIndex: 10000 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            data-oid="hnsdkym"
          />

          {/* Modal */}
          <motion.div
            className="fixed inset-0 z-[10001] flex items-center justify-center p-4"
            style={{ zIndex: 10001 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-oid="xaooggc"
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              data-oid="8hwxk_y"
            >
              {/* Header */}
              <div
                className="relative bg-gradient-to-r from-navy to-[#A38648] text-white p-6 rounded-t-2xl"
                data-oid="_ppjk.v"
              >
                <button
                  onClick={handleClose}
                  className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
                  data-oid="kh61my2"
                >
                  <X size={20} data-oid="sbi1:y4" />
                </button>

                <div className="flex items-center gap-3" data-oid="bsiwwyo">
                  <div
                    className="p-3 bg-white/20 rounded-full"
                    data-oid="xupi069"
                  >
                    <Calendar size={24} data-oid="5r.-:x." />
                  </div>
                  <div data-oid="alj:062">
                    <h2
                      className="text-2xl md:text-3xl font-playfair font-bold"
                      data-oid="iwu7p03"
                    >
                      Plan Your Event
                    </h2>
                    <p className="text-white/90 font-lato" data-oid="0q85v4r">
                      Let's create something unforgettable together
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6" data-oid="ef2kzcc">
                {/* Event Types - Compact */}
                <div className="mb-6" data-oid="m7g.yld">
                  <h3
                    className="text-lg font-playfair font-semibold text-gray-900 mb-3"
                    data-oid="u98mdgc"
                  >
                    Event Type:
                  </h3>
                  <div className="flex flex-wrap gap-2" data-oid="h1q29f:">
                    {eventTypes.map((event, index) => {
                      const IconComponent = event.icon;
                      return (
                        <motion.button
                          key={event.title}
                          onClick={() => {
                            if (typeof window !== "undefined") {
                              window.dataLayer = window.dataLayer || [];

                              // Clear all variables first (prevents data pollution)
                              window.dataLayer.push({
                                event_type: undefined,
                                event_category: undefined,
                                event_label: undefined,
                                button_text: undefined,
                                button_location: undefined,
                                consultation_method: undefined,
                                service_category: undefined,
                              });

                              // Push the event with standardized event_type values
                              window.dataLayer.push({
                                event: "event_type_selected",
                                event_type: event.eventType, // Now uses standardized values: wedding, corporate, private_party
                                event_category: "event_planning",
                                event_label: event.title,
                              });
                            }
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gray-300 hover:border-gold hover:bg-gold/5 transition-all duration-200 text-sm font-medium text-gray-700 hover:text-gray-900"
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: index * 0.05 }}
                          data-oid="r.xwsjy"
                        >
                          <IconComponent
                            size={16}
                            className="text-gold"
                            data-oid="gex-93h"
                          />

                          <span data-oid="io42.q5">{event.title}</span>
                        </motion.button>
                      );
                    })}
                  </div>
                </div>

                {/* Contact Methods */}
                <div className="mb-6" data-oid="317fkfk">
                  <h3
                    className="text-xl font-playfair font-semibold text-gray-900 mb-4"
                    data-oid="u6z7.uf"
                  >
                    Get In Touch
                  </h3>
                  <div
                    className="grid grid-cols-1 md:grid-cols-3 gap-4"
                    data-oid="97007y8"
                  >
                    {contactMethods.map((method, index) => {
                      const IconComponent = method.icon;
                      return (
                        <motion.div
                          key={method.title}
                          className={`p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-lg h-full ${
                            method.primary
                              ? "border-gold bg-gold/5"
                              : "border-gray-200 hover:border-gold/50"
                          }`}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4 + index * 0.1 }}
                          data-oid="8d_pbjc"
                        >
                          <div
                            className="flex flex-col items-center text-center"
                            data-oid="j532dmw"
                          >
                            <div
                              className={`p-3 rounded-lg mb-3 ${
                                method.primary
                                  ? "bg-gold text-white"
                                  : "bg-gray-100 text-gold"
                              }`}
                              data-oid="n5haaas"
                            >
                              <IconComponent size={24} data-oid="1quu3_9" />
                            </div>
                            <h4
                              className="font-semibold text-gray-900 mb-1"
                              data-oid="nmhzq.c"
                            >
                              {method.title}
                            </h4>
                            <p
                              className="text-xs text-gray-600 mb-3"
                              data-oid="azzhe3v"
                            >
                              {method.description}
                            </p>
                            {method.action === "#calendly" ? (
                              <Button
                                onClick={() => {
                                  trackContactAction(
                                    "event_planning",
                                    "calendly",
                                    {
                                      booking_method: "calendly",
                                    },
                                  );

                                  // Close the modal directly (don't use handleClose to avoid clearing)
                                  onClose();

                                  // Set context AFTER closing modal (so it doesn't get cleared)
                                  if (typeof window !== "undefined") {
                                    window.dataLayer = window.dataLayer || [];
                                    window.dataLayer.push({
                                      parent_modal: "event_contact",
                                      button_text: "Schedule Consultation",
                                      button_location: "event_contact_modal",
                                    });
                                  }

                                  // Open Calendly
                                  const event = new CustomEvent("openCalendly");
                                  window.dispatchEvent(event);
                                }}
                                className={`contact-action-track w-full text-sm font-medium transition-all duration-300 transform hover:scale-105 ${
                                  method.primary
                                    ? "bg-gold hover:bg-gold/90 text-white shadow-lg hover:shadow-xl"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                                data-oid="73n5ec2"
                              >
                                {method.contact}
                              </Button>
                            ) : (
                              <a
                                href={method.action}
                                onClick={() => {
                                  const actionType = method.action.startsWith(
                                    "tel:",
                                  )
                                    ? "phone"
                                    : "email";
                                  const details: Record<string, any> = {};
                                  if (actionType === "phone")
                                    details.phone_number = "+41338410021";
                                  if (actionType === "email")
                                    details.email_address =
                                      "events@beatenberg-resortspa.ch";
                                  trackContactAction(
                                    "event_planning",
                                    actionType,
                                    details,
                                  );
                                }}
                                className={`contact-action-track inline-block w-full px-3 py-2 rounded-full text-sm font-medium transition-all duration-300 transform hover:scale-105 ${
                                  method.primary
                                    ? "bg-gold hover:bg-gold/90 text-white shadow-lg hover:shadow-xl"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                                data-oid="6la3zj:"
                              >
                                {method.contact}
                              </a>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>

                {/* Event Features */}
                <div
                  className="bg-gradient-to-r from-navy/10 to-gold/10 rounded-xl p-4 mb-6"
                  data-oid="f1e3c.p"
                >
                  <h4
                    className="font-semibold text-gray-900 mb-3 text-center"
                    data-oid="dzgd-4x"
                  >
                    What We Offer
                  </h4>
                  <div
                    className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-gray-700"
                    data-oid="ouwmudy"
                  >
                    <div className="text-center" data-oid="ft9rj5_">
                      <span className="block" data-oid="t1l4462">
                        ✓ Full Event Planning
                      </span>
                    </div>
                    <div className="text-center" data-oid="pj_wsy7">
                      <span className="block" data-oid="d5195fw">
                        ✓ Gourmet Catering
                      </span>
                    </div>
                    <div className="text-center" data-oid="w.qz2.5">
                      <span className="block" data-oid="ic42zj0">
                        ✓ AV Equipment
                      </span>
                    </div>
                    <div className="text-center" data-oid="2i_rpt-">
                      <span className="block" data-oid="2bycks3">
                        ✓ 200+ Capacity
                      </span>
                    </div>
                    <div className="text-center" data-oid=".8zb86y">
                      <span className="block" data-oid="si_e7v9">
                        ✓ Alpine Views
                      </span>
                    </div>
                    <div className="text-center" data-oid="wmk5jw5">
                      <span className="block" data-oid="_brns67">
                        ✓ Accommodation
                      </span>
                    </div>
                    <div className="text-center" data-oid="sy5tbo8">
                      <span className="block" data-oid="zca4h1u">
                        ✓ Parking (40+ cars)
                      </span>
                    </div>
                    <div className="text-center" data-oid="ea.jfaz">
                      <span className="block" data-oid="a1bj5d0">
                        ✓ Flexible Spaces
                      </span>
                    </div>
                  </div>
                </div>

                {/* Close Button */}
                <div className="flex justify-center" data-oid=".5fu2eu">
                  <Button
                    onClick={handleClose}
                    variant="outline"
                    className="px-8"
                    data-oid="r8cd:r8"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default EventContactModal;
